import React, { useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Task } from '../../../../types/task';
import { getTaskCompactTimeText, getTaskTimeStatus, getTaskTimeInfo } from '../../../../utils/taskSortUtils';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';

interface TaskItemV2Props {
  task: Task;
  onToggle: (taskId: string, completed: boolean) => void;
  onClick: (taskId: string) => void;
  viewMode?: 'list' | 'timeline';
  timelineHeight?: number; // 时间轴模式下的高度（像素）
  showDatePrefix?: boolean; // 是否在时间前显示日期前缀（用于过去所有/未来所有模式）
  isFocused?: boolean; // 是否处于键盘焦点状态
  isTemporaryVisible?: boolean; // 是否为临时显示的任务
  isCompletedToday?: boolean; // 是否为完成于今天的其他日期任务
  compact?: boolean; // 是否使用紧凑模式（只显示标题和重要性圆点）
  showBackground?: boolean; // 是否显示背景色（默认true）
  hasDragged?: boolean; // 是否进行了拖拽操作（用于区分拖拽和点击）
}

/**
 * TaskV2 简化任务项组件
 * 采用无边界设计，信息精简，支持等高和时间轴两种模式
 */
export const TaskItemV2: React.FC<TaskItemV2Props> = ({
  task,
  onToggle,
  onClick,
  viewMode = 'list',
  timelineHeight,
  showDatePrefix = false,
  isFocused = false,
  isTemporaryVisible = false,
  isCompletedToday = false,
  compact = false,
  showBackground = true,
  hasDragged = false
}) => {
  const { t } = useTranslation(['v3/tasks']);
  const [isDark] = useAtom(isDarkAtom);

  // 获取任务重要性颜色（用于圆圈）
  const getImportanceColor = useCallback(() => {
    if (task.vital && task.urgent) {
      return 'border-red-500'; // 重要且紧急 - 红色
    } else if (task.vital) {
      return 'border-orange-500'; // 重要不紧急 - 橙色
    } else if (task.urgent) {
      return 'border-yellow-500'; // 紧急不重要 - 黄色
    } else {
      return 'border-gray-400'; // 普通 - 灰色
    }
  }, [task.vital, task.urgent]);

  // 处理复选框点击
  const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(task.id, !task.done);
  }, [task.id, task.done, onToggle]);

  // 处理任务项点击
  const handleItemClick = useCallback(() => {
    // 如果刚刚进行了拖拽操作，忽略点击事件
    if (hasDragged) {
      console.log('检测到拖拽操作，忽略点击事件', { taskId: task.id });
      return;
    }

    onClick(task.id);
  }, [task.id, onClick, hasDragged]);

  // 获取带日期前缀的时间显示文本
  const getTaskCompactTimeTextWithDate = useCallback((task: Task): string => {
    const timeInfo = getTaskTimeInfo(task);

    if (!timeInfo.hasTime) {
      return '';
    }

    const parts: string[] = [];

    // 1. 日期前缀
    if (task.startAt || task.dueAt) {
      const dateToUse = task.startAt || task.dueAt;
      const date = new Date(dateToUse!);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const taskDate = new Date(date);
      taskDate.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      // 判断是否跨年
      const currentYear = today.getFullYear();
      const taskYear = date.getFullYear();
      const isCurrentYear = taskYear === currentYear;

      let datePrefix = '';
      if (taskDate.getTime() === today.getTime()) {
        datePrefix = '今天';
      } else if (taskDate.getTime() === tomorrow.getTime()) {
        datePrefix = '明天';
      } else if (taskDate.getTime() === yesterday.getTime()) {
        datePrefix = '昨天';
      } else {
        // 显示月日或年月日
        if (isCurrentYear) {
          datePrefix = date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
        } else {
          datePrefix = date.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
        }
      }
      parts.push(datePrefix);
    }

    // 2. 开始时间 (startAt)
    if (task.startAt) {
      const startTime = new Date(task.startAt);
      const timeStr = startTime.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      parts.push(timeStr);
    }

    // 3. 时长 (cost)
    if (task.cost && task.cost > 0) {
      const minutes = Math.floor(task.cost / 60);
      const hours = Math.floor(minutes / 60);

      if (hours > 0) {
        const remainingMinutes = minutes % 60;
        const durationStr = remainingMinutes > 0 ? `${hours}h${remainingMinutes}m` : `${hours}h`;
        parts.push(durationStr);
      } else {
        parts.push(`${minutes}m`);
      }
    }

    // 4. 截止时间 (dueAt) - 只有当它与开始时间不同时才显示
    if (task.dueAt && task.dueAt !== task.startAt) {
      const dueTime = new Date(task.dueAt);
      const dueTimeStr = dueTime.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // 如果有开始时间，用箭头表示时间范围
      if (task.startAt) {
        parts.push(`→${dueTimeStr}`);
      } else {
        // 只有截止时间，用 due: 前缀
        parts.push(`due:${dueTimeStr}`);
      }
    }

    return parts.join(' ');
  }, []);

  // 获取时间显示文本（原始时间）
  const timeDisplayText = useMemo(() => {
    let baseText = '';
    if (isTemporaryVisible || showDatePrefix) {
      baseText = getTaskCompactTimeTextWithDate(task);
    } else {
      baseText = getTaskCompactTimeText(task);
    }

    return baseText;
  }, [task, showDatePrefix, getTaskCompactTimeTextWithDate, isTemporaryVisible]);

  // 获取完成时间显示文本
  const completedTimeText = useMemo(() => {
    if (!isCompletedToday || !task.doneAt) return '';

    const doneTime = new Date(task.doneAt);
    const hours = doneTime.getHours().toString().padStart(2, '0');
    const minutes = doneTime.getMinutes().toString().padStart(2, '0');
    return `完成于 ${hours}:${minutes}`;
  }, [isCompletedToday, task.doneAt]);

  // 获取任务时间状态
  const timeStatus = useMemo(() => {
    return getTaskTimeStatus(task);
  }, [task]);

  // 获取时间显示颜色
  const getTimeColor = useCallback(() => {
    // 已完成任务统一使用浅色
    if (task.done) {
      return 'text-gray-300';
    }

    switch (timeStatus) {
      case 'overdue':
        return 'text-red-500'; // 保留红色过期提醒
      case 'today':
      case 'upcoming':
      default:
        return 'text-gray-500'; // 其他都统一为灰色
    }
  }, [timeStatus, task.done]);

  // 时间轴模式的样式
  const timelineStyle = useMemo(() => {
    if (viewMode === 'timeline' && timelineHeight) {
      return {
        height: `${timelineHeight}px`,
        minHeight: '44px' // 最小高度确保可点击
      };
    }
    return {};
  }, [viewMode, timelineHeight]);

  return (
    <div
      className={`
        flex cursor-pointer transition-all duration-200
        ${compact ? 'px-3 py-2' : 'px-4 py-3'}
        ${viewMode === 'timeline'
          ? `border-l-2 relative items-start ${isDark ? 'border-ios-dark-gray4' : 'border-gray-200'}`
          : `items-center`
        }
        ${showBackground
          ? isFocused
            ? isDark
              ? 'bg-blue-900/30'
              : 'bg-blue-100'
            : viewMode === 'list'
              ? isDark
                ? 'bg-ios-dark-gray6 hover:bg-ios-dark-gray5'
                : 'bg-white hover:bg-gray-50'
              : viewMode === 'timeline'
                ? isDark
                  ? 'bg-blue-200/5 hover:bg-blue-200/10'
                  : 'bg-blue-200/10 hover:bg-blue-200/15'
                : ''
          : isFocused
            ? isDark
              ? 'bg-blue-900/30'
              : 'bg-blue-100'
            : 'hover:bg-gray-50/50'
        }
      `}
      style={timelineStyle}
      onClick={handleItemClick}
    >
      {/* 时间轴模式的时间标记 */}
      {viewMode === 'timeline' && (
        <div className={`absolute left-0 top-4 w-2 h-2 rounded-full transform -translate-x-1/2 ${
          isDark ? 'bg-ios-dark-gray3' : 'bg-gray-400'
        }`}></div>
      )}

      {/* 复选框圆圈 */}
      <button
        onClick={handleCheckboxClick}
        className={`
          flex-shrink-0 rounded-full border-2
          flex items-center justify-center transition-all duration-200
          ${compact ? 'w-4 h-4 mr-2' : 'w-5 h-5 mr-3'}
          ${task.done ? 'border-gray-300 bg-gray-300' : getImportanceColor()}
          ${!task.done ? 'bg-transparent' : ''}
          hover:scale-110
        `}
      >
        {task.done && (
          <svg
            className={`text-white ${compact ? 'w-2 h-2' : 'w-3 h-3'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>

      {/* 任务内容 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          {/* 任务内容 */}
          <div className="flex-1 min-w-0">
            {/* 任务标题 */}
            <div className="flex items-center gap-2">
              <div
                className={`
                  font-medium truncate
                  ${compact ? 'text-sm' : 'text-base'}
                  ${task.done
                    ? `line-through ${isDark ? 'text-ios-dark-gray2' : 'text-gray-500'}`
                    : isDark ? 'text-ios-dark-gray1' : 'text-gray-900'
                  }
                `}
              >
                {task.title}
              </div>

              {/* 临时显示提示 - 只对未完成的任务显示，紧凑模式下不显示 */}
              {!compact && isTemporaryVisible && !task.done && (
                <span className={`text-xs italic ${isDark ? 'text-ios-dark-gray3' : 'text-gray-400'}`}>
                  {t('v3/tasks:temporaryTask.hint', '刷新或切换日期后消失')}
                </span>
              )}
            </div>

            {/* 时间和项目信息 - 紧凑模式下不显示 */}
            {!compact && (
              <div className="flex flex-wrap items-center mt-1 gap-x-3 gap-y-1">
              {/* 原始时间信息 */}
              {timeDisplayText && (
                <div className="flex items-center flex-shrink-0">
                  <svg
                    className={`w-3 h-3 mr-1 ${
                      task.done
                        ? isDark ? 'text-ios-dark-gray3' : 'text-gray-400'
                        : isDark ? 'text-ios-dark-gray2' : 'text-gray-500'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="12" cy="12" r="9" strokeWidth="2"/>
                    <path d="M12 7v5l3 3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className={`text-xs ${getTimeColor()}`}>
                    {timeDisplayText}
                  </span>
                </div>
              )}

              {/* 完成时间信息 */}
              {completedTimeText && (
                <div className="flex items-center flex-shrink-0">
                  <svg
                    className={`w-3 h-3 mr-1 ${isDark ? 'text-ios-dark-gray3' : 'text-gray-500'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="9" strokeWidth="2"/>
                  </svg>
                  <span className={`text-xs ${isDark ? 'text-ios-dark-gray3' : 'text-gray-500'}`}>
                    {completedTimeText}
                  </span>
                </div>
              )}

              {/* 项目名称 */}
              {task.project?.title && (
                <div className="flex items-center flex-shrink-0">
                  <svg
                    className={`w-3 h-3 mr-1 ${
                      task.done
                        ? isDark ? 'text-ios-dark-gray3' : 'text-gray-400'
                        : isDark ? 'text-ios-dark-gray2' : 'text-gray-500'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" strokeWidth="2"/>
                    <path d="M8 21v-4a2 2 0 012-2h4a2 2 0 012 2v4" strokeWidth="2"/>
                  </svg>
                  <span className={`text-xs ${
                    task.done
                      ? isDark ? 'text-ios-dark-gray3' : 'text-gray-400'
                      : isDark ? 'text-ios-dark-gray2' : 'text-gray-500'
                  }`}>
                    {task.project.title}
                  </span>
                </div>
              )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
